import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  HelpCircle, FileText, Video, Users, TrendingUp, Settings,
  ChevronRight, Download, Building2, Mail, Phone, ArrowLeft,
  Lightbulb, CheckCircle, Shield, CreditCard
} from 'lucide-react';
import CorporateProposal from '../components/CorporateProposal';
import { useAuth } from '../context/AuthContext';

const helpCategories = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: <Lightbulb className="w-5 h-5" />,
    color: 'bg-amber-500/20 text-amber-400',
    questions: [
      { q: 'What is BrandStage?', a: 'BrandStage is a video-first platform that helps businesses build their digital presence through authentic leadership narratives, professional company profiles, and measurable digital engagement.' },
      { q: 'How do I create my first profile?', a: 'Click "Create Company Profile" from the homepage or dashboard. Fill in your company details, add your story, and optionally upload videos. You can save as draft and complete later.' },
      { q: 'Is BrandStage free to use?', a: 'Creating a profile is free. To make your profile publicly visible and access premium features like BrandStage Connect, you\'ll need an active subscription.' },
      { q: 'What makes BrandStage different?', a: 'We focus on video-first storytelling. While others offer text profiles, we believe authentic leadership voice—captured on video—builds trust that text alone cannot achieve.' }
    ]
  },
  {
    id: 'profiles',
    title: 'Company & Leadership Profiles',
    icon: <Building2 className="w-5 h-5" />,
    color: 'bg-[#C41E3A]/20 text-[#C41E3A]',
    questions: [
      { q: 'What should I include in my company profile?', a: 'Focus on your unique story, mission, key differentiators, and leadership team. Add videos of founders or leaders speaking authentically about your vision.' },
      { q: 'Can I have multiple profiles?', a: 'Yes, you can create separate company profiles and leadership profiles. Many organisations create one company profile with multiple linked leadership profiles.' },
      { q: 'What is the Digital Readiness Score?', a: 'A benchmark that measures how complete and engaging your digital presence is. It considers profile completeness, video content, social links, and engagement factors.' },
      { q: 'How do I improve my readiness score?', a: 'Add more content, include videos, link your social profiles, add testimonials, and keep your information up to date. The score updates automatically.' },
      { q: 'Can I edit my profile after publishing?', a: 'Yes, you can edit anytime from your dashboard. Changes are saved immediately.' }
    ]
  },
  {
    id: 'videos',
    title: 'Video Recording & Content',
    icon: <Video className="w-5 h-5" />,
    color: 'bg-blue-500/20 text-blue-400',
    questions: [
      { q: 'What is a Quick Bite?', a: 'A 30-40 second video clip you can record directly from your browser. Perfect for testimonials, product highlights, or social media content.' },
      { q: 'How do I record a Quick Bite?', a: 'Go to "Record Quick Bite" from the menu. Allow camera/microphone access, and click record. Your video is processed and delivered to your email within seconds.' },
      { q: 'What are the best practices for recording?', a: 'Good lighting (face a window), quiet environment, look at the camera, speak clearly, and keep it concise. Authenticity matters more than perfection. Check our Video Guidelines for detailed standards.' },
      { q: 'Can I use my own videos?', a: 'Yes, you can upload existing videos to your profile. We support common formats like MP4, MOV, and WebM. Please review our Video Guidelines to ensure your content meets our quality standards.' },
      { q: 'What about professional video production?', a: 'We offer customised video production services including leadership interviews, trade show coverage, and corporate films through our partner Cinimage.' },
      { q: 'Where can I find video quality standards?', a: 'Visit our Video Guidelines page for detailed technical requirements, visual standards, and a self-assessment checklist. This covers both BrandStage productions and self-uploaded content.' }
    ]
  },
  {
    id: 'connect',
    title: 'BrandStage Connect (Coming Soon)',
    icon: <Users className="w-5 h-5" />,
    color: 'bg-green-500/20 text-green-400',
    questions: [
      { q: 'What is BrandStage Connect?', a: 'A curated network where verified members will share and discover business opportunities—partnerships, collaborations, speaking engagements, and more. Coming soon once we reach 50+ active profiles.' },
      { q: 'When will Connect launch?', a: 'BrandStage Connect is currently in development and will launch once we have a critical mass of active profiles to ensure a meaningful experience for all members.' },
      { q: 'How will I access Connect?', a: 'Connect will be available to members with active profile subscriptions. We\'ll notify all subscribers when it goes live.' },
      { q: 'Can I express interest early?', a: 'Yes — contact us at operations@brandstage-media.com to register your interest. Early registrants may get priority access.' }
    ]
  },
  {
    id: 'subscription',
    title: 'Subscription & Payments',
    icon: <CreditCard className="w-5 h-5" />,
    color: 'bg-purple-500/20 text-purple-400',
    questions: [
      { q: 'What subscription options are available?', a: 'We offer 6-month and 1-year profile subscriptions, plus the Connect add-on. Corporate clients can also opt for customised annual retainers.' },
      { q: 'What payment methods do you accept?', a: 'We accept UPI, credit/debit cards, netbanking, and wallets through our secure Razorpay integration. International payments are accepted in USD.' },
      { q: 'Can I get an invoice?', a: 'Yes, invoices are automatically generated for all payments and available from your dashboard. GST is included for Indian clients.' },
      { q: 'What happens when my subscription expires?', a: 'Your profile becomes private (not publicly visible). Your content is preserved, and you can reactivate anytime.' }
    ]
  }
];

export default function HelpPage() {
  const { user } = useAuth();
  const [activeCategory, setActiveCategory] = useState('getting-started');
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  const [showProposal, setShowProposal] = useState(false);

  const currentCategory = helpCategories.find(c => c.id === activeCategory);

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#C41E3A] to-[#8B1428] py-16">
        <div className="max-w-7xl mx-auto px-6">
          <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight mb-4">Help Center</h1>
          <p className="text-white/80 text-lg max-w-2xl">
            Find answers to common questions and learn how to make the most of BrandStage.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar - Categories */}
          <div className="lg:col-span-1">
            <div className="sticky top-6 space-y-2">
              <p className="text-xs uppercase tracking-wider text-zinc-500 mb-4">Categories</p>
              {helpCategories.map(category => (
                <button
                  key={category.id}
                  onClick={() => {
                    setActiveCategory(category.id);
                    setExpandedQuestion(null);
                    setShowProposal(false);
                  }}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all text-left ${
                    activeCategory === category.id && !showProposal
                      ? 'bg-zinc-800 text-white'
                      : 'text-zinc-400 hover:bg-zinc-800/50 hover:text-white'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${category.color}`}>
                    {category.icon}
                  </div>
                  <span className="text-sm font-medium">{category.title}</span>
                </button>
              ))}

              {/* Corporate Proposal Download */}
              <div className="pt-6 mt-6 border-t border-zinc-800">
                <p className="text-xs uppercase tracking-wider text-zinc-500 mb-4">Resources</p>
                <button
                  onClick={() => setShowProposal(true)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all text-left ${
                    showProposal
                      ? 'bg-zinc-800 text-white'
                      : 'text-zinc-400 hover:bg-zinc-800/50 hover:text-white'
                  }`}
                >
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#C41E3A]/20 text-[#C41E3A]">
                    <Download className="w-5 h-5" />
                  </div>
                  <span className="text-sm font-medium">Corporate Proposal</span>
                </button>
                
                <Link
                  to="/video-guidelines"
                  className="w-full flex items-center gap-3 p-3 rounded-lg transition-all text-left text-zinc-400 hover:bg-zinc-800/50 hover:text-white mt-2"
                >
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-blue-500/20 text-blue-400">
                    <Video className="w-5 h-5" />
                  </div>
                  <span className="text-sm font-medium">Video Guidelines</span>
                </Link>
              </div>

              {/* Contact Support */}
              <div className="pt-6 mt-6 border-t border-zinc-800">
                <p className="text-xs uppercase tracking-wider text-zinc-500 mb-4">Need More Help?</p>
                <a
                  href="mailto:support@brandstage-media.com"
                  className="flex items-center gap-3 p-3 rounded-lg bg-zinc-800/50 hover:bg-zinc-800 transition-colors"
                >
                  <Mail className="w-5 h-5 text-[#C41E3A]" />
                  <div>
                    <p className="text-sm font-medium">Contact Support</p>
                    <p className="text-xs text-zinc-500">support@brandstage-media.com</p>
                  </div>
                </a>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {showProposal ? (
              <CorporateProposal 
                companyName={user?.company_name || '[Your Company]'} 
                contactName={user?.name || '[Your Name]'} 
              />
            ) : (
              <div>
                {/* Category Header */}
                <div className="flex items-center gap-4 mb-8">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${currentCategory?.color}`}>
                    {currentCategory?.icon}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">{currentCategory?.title}</h2>
                    <p className="text-zinc-400">{currentCategory?.questions.length} questions</p>
                  </div>
                </div>

                {/* Questions */}
                <div className="space-y-3">
                  {currentCategory?.questions.map((item, index) => (
                    <div 
                      key={index}
                      className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden"
                    >
                      <button
                        onClick={() => setExpandedQuestion(expandedQuestion === index ? null : index)}
                        className="w-full p-5 text-left flex items-start gap-4 hover:bg-zinc-800/50 transition-colors"
                      >
                        <HelpCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 transition-colors ${
                          expandedQuestion === index ? 'text-[#C41E3A]' : 'text-zinc-500'
                        }`} />
                        <span className={`font-medium flex-1 transition-colors ${
                          expandedQuestion === index ? 'text-white' : 'text-zinc-300'
                        }`}>
                          {item.q}
                        </span>
                        <ChevronRight className={`w-5 h-5 text-zinc-500 transition-transform ${
                          expandedQuestion === index ? 'rotate-90' : ''
                        }`} />
                      </button>
                      
                      {expandedQuestion === index && (
                        <div className="px-5 pb-5 pl-14">
                          <p className="text-zinc-400 leading-relaxed">{item.a}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
